import React, { Component } from 'react';
import { options } from './keys';
import { flattenChildren, getParents, updateHrefs } from './helpers';
import { withTranslation } from 'react-i18next';
import { isTrue } from '../../helpers/isTrue';
import { getUpgradeUrl } from '../../helpers/upgradeUrl';

class Sidebar extends Component {
    constructor(props) {
        super();

        this.onSelect = this.onSelect.bind(this);

        this.state = {
            selectedIds: [],
            defaultSelected: [],
        };
    }

    // `default_domain_id` is resolved asynchronously by Layout (from /api/v1/me/), so the
    // domain id must be derived from the current props on every render instead of being
    // frozen at construction time.
    get domainId() {
        return this.props.domain_id || this.props.default_domain_id;
    }

    componentDidMount() {
        this.syncSelectedItem();
    }

    componentDidUpdate(prevProps) {
        const prevDomainId = prevProps.domain_id || prevProps.default_domain_id;
        if (prevDomainId !== this.domainId) this.syncSelectedItem();
    }

    syncSelectedItem() {
        const allOptions = options({
            domainId: this.domainId,
            t: this.props.t,
        });
        const all = flattenChildren(allOptions);
        const selectedItem = all.find((item) => {
            if (item.hrefStartsWith)
                return window.location?.pathname.startsWith(item.hrefStartsWith);

            if (item.pathnames) return item.pathnames.includes(window.location?.pathname);

            return item.href === window.location?.pathname;
        });
        const parents = getParents(all, selectedItem);
        const reversed = parents.reverse();
        if (selectedItem)
            this.setState({
                selectedIds: [...reversed, selectedItem.id],
                defaultSelected: [...reversed, selectedItem.id],
                selectedUrl: selectedItem.id,
            });
    }

    onSelect(id, { clearAllSelected }) {
        if (clearAllSelected) {
            const ids = this.state.selectedIds.includes(id) ? [] : [id];
            return this.setState({
                selectedIds: ids,
            });
        }
        const ids = this.state.selectedIds.includes(id)
            ? this.state.selectedIds.filter((item) => item !== id)
            : [this.state.selectedIds[0], id];

        this.setState({
            selectedIds: ids,
        });
    }

    collectData(options) {
        return options.map(
            (item) =>
                !item.isHidden && (
                    <React.Fragment key={item.id}>
                        {item.href && (
                            <button
                                title={item.title}
                                className={`list-group-item discovery-item  ${
                                    this.state.selectedUrl === item.id
                                        ? 'default-selected-item'
                                        : ''
                                }`}
                            >
                                <a href={item.href}>
                                    {item.icon && <i className={item.icon}></i>}
                                    <span className="collaps-btn sidebar-item-title">
                                        {item.title}
                                    </span>
                                </a>
                            </button>
                        )}
                        {item.disabled && !item.href && (
                            // A report that is not built yet: the item is still listed so the
                            // full tools structure is visible, but it is rendered as plain text
                            // instead of a link until an `href` is added to `keys.js`.
                            <button
                                title={item.title}
                                className="list-group-item discovery-item is-disabled"
                                aria-disabled="true"
                            >
                                <a>
                                    {item.icon && <i className={item.icon}></i>}
                                    <span className="collaps-btn sidebar-item-title">
                                        {item.title}
                                    </span>
                                </a>
                            </button>
                        )}
                        {item.collapsTargetId && (
                            <div id={item.id}>
                                <button
                                    className={`collapsed list-group-item discovery-item panel-heading ${
                                        this.state.selectedIds.includes(item.id) ? 'active' : ''
                                    }`}
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#${item.collapsTargetId}`}
                                    aria-expanded="false"
                                    aria-controls={item.collapsTargetId}
                                    onClick={() => this.onSelect(item.id, item)}
                                    title={item.title}
                                >
                                    <span className="collaps-btn">
                                        {item.icon && <i className={item.icon}></i>}
                                        <span className="sidebar-item-title">{item.title}</span>
                                    </span>
                                </button>
                                <div
                                    id={item.collapsTargetId}
                                    className={`collapse panel-collapse ${
                                        this.state.defaultSelected.includes(item.id) ? 'show' : ''
                                    }`}
                                    aria-labelledby="headingTwo"
                                    data-bs-parent={`#${item.parent ?? 'accordion'}`}
                                >
                                    <div className="pl-20">{this.collectData(item.children)}</div>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                ),
        );
    }

    render() {
        let allOptions = options({
            domainId: this.domainId,
            t: this.props.t,
        });

        if (!this.props.show_discover_analytics && !isTrue(window.is_superuser)) {
            allOptions = allOptions.map((option) => {
                if (option.id === 'discover_analytics') {
                    return {
                        ...option,
                        children: updateHrefs(
                            option.children,
                            getUpgradeUrl('/domains/inactive-subscription?excluded=discover_analytics'),
                        ),
                    };
                }
                if (option.id === 'opportunities') {
                    return {
                        ...option,
                        children: option.children.map((child) => {
                            if (child.id === 'opportunities_Discover') {
                                return {
                                    ...child,
                                    href: getUpgradeUrl('/domains/inactive-subscription?excluded=discover_analytics'),
                                };
                            }
                            return child;
                        }),
                    };
                }
                return option;
            });
        }
        if (!this.props.show_gsc_data && !isTrue(window.is_superuser)) {
            allOptions = allOptions.map((option) => {
                if (option.id === 'search_analytics') {
                    return {
                        ...option,
                        children: updateHrefs(
                            option.children,
                            getUpgradeUrl('/domains/inactive-subscription?excluded=search_analytics'),
                        ),
                    };
                }
                if (option.id === 'googleSurfaces') {
                    return {
                        ...option,
                        href: getUpgradeUrl('/domains/inactive-subscription?excluded=search_analytics'),
                    };
                }
                if (option.id === 'opportunities') {
                    return {
                        ...option,
                        children: option.children.map((child) => {
                            if (child.id === 'opportunities_SEO_Recommendations') {
                                return {
                                    ...child,
                                    href: getUpgradeUrl('/domains/inactive-subscription?excluded=search_analytics'),
                                };
                            }
                            return child;
                        }),
                    };
                }
                return option;
            });
        }
        if (!this.props.show_commerce && !isTrue(window.is_superuser)) {
            allOptions = allOptions.map((option) => {
                if (option.id === 'commerce') {
                    return {
                        ...option,
                        href: getUpgradeUrl('/domains/inactive-subscription?excluded=commerce'),
                    };
                }
                return option;
            });
        }
        if (!this.props.show_ai_tracking && !isTrue(window.is_superuser)) {
            allOptions = allOptions.map((option) => {
                if (option.id === 'ai_tracking') {
                    return {
                        ...option,
                        children: updateHrefs(
                            option.children,
                            getUpgradeUrl('/domains/inactive-subscription?excluded=ai_tracking'),
                        ),
                    };
                }
                return option;
            });
        }
        return (
            <div id="accordion">
                {this.collectData(allOptions)}
                <div className="sidebar-bottom">
                    <button
                        className="btn-icon mini-menu-toggle-btn"
                        onClick={() => this.props.updateStyle('mini-menu')}
                    >
                        <i className="icon-chevron-left-double" />
                    </button>
                    <button
                        className="btn-icon"
                        onClick={() => this.props.updateStyle('menu-version')}
                    >
                        <i className="icon-u_top-arrow-to-top-path2" />
                    </button>
                </div>
            </div>
        );
    }
}

export default withTranslation()(Sidebar);
